/**
 * PerformanceMonitor - Debug overlay for FPS and pool statistics
 *
 * Features:
 * - FPS counter (current, min, avg)
 * - Active bullet count
 * - Active enemy count
 * - Pool utilization percentages
 * - Toggle with 'P' key (dev only)
 *
 * Only visible in development mode.
 */

import Phaser from 'phaser';
import { GAME_WIDTH, SAFE_AREA } from '../config';
import { getParticlePool } from '../systems/ParticlePool';

interface PoolStats {
  bulletPool?: {
    currentActive: number;
    totalSize: number;
    utilization: number;
  };
  spatialGrid?: {
    occupiedCells: number;
    totalEntities: number;
  };
}

export class PerformanceMonitor {
  private scene: Phaser.Scene;

  /** Container for all monitor elements */
  private container: Phaser.GameObjects.Container;

  /** Background panel */
  private background: Phaser.GameObjects.Graphics;

  /** Text displays */
  private fpsText: Phaser.GameObjects.Text;
  private bulletText: Phaser.GameObjects.Text;
  private enemyText: Phaser.GameObjects.Text;
  private poolText: Phaser.GameObjects.Text;

  /** FPS tracking */
  private fpsHistory: number[] = [];
  private minFps: number = 60;
  private updateInterval: number = 250; // Update every 250ms
  private lastUpdate: number = 0;

  /** Whether the monitor is visible */
  private isVisible: boolean = false;

  /** External stats provider functions */
  private getPoolStats: (() => PoolStats) | null = null;
  private getEnemyCount: (() => number) | null = null;

  /** Key binding */
  private toggleKey: Phaser.Input.Keyboard.Key | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;

    // Create container (hidden by default)
    this.container = scene.add.container(GAME_WIDTH - 160, SAFE_AREA.top + 60);
    this.container.setDepth(1000); // Always on top
    this.container.setVisible(false);

    // Create background panel
    this.background = scene.add.graphics();
    this.background.fillStyle(0x000000, 0.7);
    this.background.fillRoundedRect(0, 0, 150, 120, 8);
    this.container.add(this.background);

    // Create text displays
    const textStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontFamily: 'monospace',
      fontSize: '11px',
      color: '#00FF00',
    };

    this.fpsText = scene.add.text(8, 8, 'FPS: --', textStyle);
    this.bulletText = scene.add.text(8, 28, 'Bullets: --', textStyle);
    this.enemyText = scene.add.text(8, 48, 'Enemies: --', textStyle);
    this.poolText = scene.add.text(8, 68, 'Pools: --', textStyle);

    this.container.add([this.fpsText, this.bulletText, this.enemyText, this.poolText]);

    // Set up keyboard toggle (only in dev mode)
    if (import.meta.env.DEV) {
      this.toggleKey = scene.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.P) ?? null;
      if (this.toggleKey) {
        this.toggleKey.on('down', () => this.toggle());
      }
    }
  }

  /**
   * Sets the function to get pool statistics
   */
  setPoolStatsProvider(fn: () => PoolStats): void {
    this.getPoolStats = fn;
  }

  /**
   * Sets the function to get enemy count
   */
  setEnemyCountProvider(fn: () => number): void {
    this.getEnemyCount = fn;
  }

  /**
   * Updates the monitor display
   * Call this from the scene's update loop
   */
  update(time: number): void {
    if (!this.isVisible) return;

    // Only update display periodically to reduce overhead
    if (time - this.lastUpdate < this.updateInterval) return;
    this.lastUpdate = time;

    // Update FPS
    const fps = Math.round(this.scene.game.loop.actualFps);
    this.fpsHistory.push(fps);
    if (this.fpsHistory.length > 20) {
      this.fpsHistory.shift();
    }

    if (fps < this.minFps && fps > 0) {
      this.minFps = fps;
    }

    const avgFps = Math.round(
      this.fpsHistory.reduce((a, b) => a + b, 0) / this.fpsHistory.length
    );

    // Color code FPS (green > 30, yellow 20-30, red < 20)
    let fpsColor = '#00FF00';
    if (fps < 20) fpsColor = '#FF4444';
    else if (fps < 30) fpsColor = '#FFFF00';

    this.fpsText.setText(`FPS: ${fps} (min:${this.minFps} avg:${avgFps})`);
    this.fpsText.setColor(fpsColor);

    // Update pool stats
    if (this.getPoolStats) {
      const stats = this.getPoolStats();

      if (stats.bulletPool) {
        this.bulletText.setText(
          `Bullets: ${stats.bulletPool.currentActive}/${stats.bulletPool.totalSize} (${stats.bulletPool.utilization.toFixed(0)}%)`
        );
      }

      if (stats.spatialGrid) {
        this.poolText.setText(
          `Grid: ${stats.spatialGrid.occupiedCells} cells, ${stats.spatialGrid.totalEntities} ents`
        );
      }
    }

    // Update particle pool stats
    const particlePool = getParticlePool();
    if (particlePool) {
      const pStats = particlePool.getStats();
      // Append to pool text
      const currentText = this.poolText.text;
      this.poolText.setText(currentText + `\nParticles: ${pStats.currentActive}/${pStats.totalSize}`);
    }

    // Update enemy count
    if (this.getEnemyCount) {
      const enemyCount = this.getEnemyCount();
      this.enemyText.setText(`Enemies: ${enemyCount}`);
    }
  }

  /**
   * Toggles the monitor visibility
   */
  toggle(): void {
    this.isVisible = !this.isVisible;
    this.container.setVisible(this.isVisible);

    if (this.isVisible) {
      // Reset min FPS when showing
      this.minFps = 60;
      this.fpsHistory = [];
    }
  }

  /**
   * Shows the monitor
   */
  show(): void {
    this.isVisible = true;
    this.container.setVisible(true);
  }

  /**
   * Hides the monitor
   */
  hide(): void {
    this.isVisible = false;
    this.container.setVisible(false);
  }

  /**
   * Returns whether the monitor is visible
   */
  getIsVisible(): boolean {
    return this.isVisible;
  }

  /**
   * Resets statistics
   */
  reset(): void {
    this.minFps = 60;
    this.fpsHistory = [];
  }

  /**
   * Cleanup
   */
  destroy(): void {
    if (this.toggleKey) {
      this.toggleKey.removeAllListeners();
    }
    this.container.destroy();
  }
}
